import type { SearchState } from '@reducers/search';
import {
  useBreadcrumbForAd,
  type AutoData,
  type MotoData,
} from '@sbt-web/breadcrumbs';
import { CategoryID, GeoEntry } from '@sbt-web/network/types';
import { TuttaItalia } from '@sbt-web/networking';
import {
  bodyValuesToName,
  fuelValuesToName,
  makeValuesToName,
  modelValuesToName,
} from '@shared/tools/search/pretty-urls/redirection-maps/auto';
import {
  adTypesLabelsMap,
  adTypesToSlugMap,
} from '@shared/tools/search/values';
import classnames from 'classnames';
import React from 'react';

import ownClasses from './ListingBreadcrumbs.module.scss';

const autoDataFromFilters = (
  filters: Record<string, string>,
  filtersLabels: Record<string, string>
): AutoData => {
  const [brand, model, type, fuel] = (
    [
      ['cb', makeValuesToName],
      ['cm', modelValuesToName],
      ['ct', bodyValuesToName],
      ['fl', fuelValuesToName],
    ] as [string, Map<string, string>][]
  ).map(([keyName, valueNameMap]) => {
    if (
      keyName in filters &&
      keyName in filtersLabels &&
      valueNameMap.has(filters[keyName])
    ) {
      return {
        label: filtersLabels[keyName],
        value: valueNameMap.get(filters[keyName]) as string,
      };
    }
    return undefined;
  });

  return { for: 'auto', brand, model, type, fuel };
};

const motoDataFromFilters = (
  filters: Record<string, string>,
  filtersLabels: Record<string, string>
): MotoData => {
  const [brand, model, type] = ['bb', 'bm', 'mt'].map((parameterName) => {
    if (parameterName in filters) {
      return {
        id: filters[parameterName],
        label: filtersLabels[parameterName],
        parameterName,
      };
    } else {
      return undefined;
    }
  });

  return { for: 'moto', brand, model, type };
};

interface Props {
  search: SearchState;
}

const ListingBreadcrumbs: React.FunctionComponent<Props> = ({ search }) => {
  let vehicleData: AutoData | MotoData | undefined = undefined;
  if (search.category.id === CategoryID.Auto) {
    vehicleData = autoDataFromFilters(search.filters, search.filtersLabels);
  } else if (search.category.id === CategoryID.Moto) {
    vehicleData = motoDataFromFilters(search.filters, search.filtersLabels);
  }

  let selectedGeo: GeoEntry | undefined = search.geo;
  if (search.geo.region.id === TuttaItalia.id) {
    selectedGeo = undefined;
  }

  const { breadcrumbs } = useBreadcrumbForAd({
    assetsBase: process.env.NEXT_PUBLIC_ASSETS_BASE_URL,
    adTypeData: {
      id: search.adType,
      label: adTypesLabelsMap[search.adType],
      parameterName: adTypesToSlugMap[search.adType],
    },
    origin: process.env.NEXT_PUBLIC_ENV_BASE_URL,
    categoryId: search.category.id,
    searchQuery: search.query,
    geo: selectedGeo,
    vehicleData,
  });

  return (
    <div
      className={classnames(
        'breadcrumbs-listing-container',
        ownClasses['breadcrumbs-listing-container']
      )}
    >
      {breadcrumbs}
    </div>
  );
};

export default ListingBreadcrumbs;
